
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface LeadImage {
  id: string;
  leadId: string;
  imageUrl: string;
  isPrimary: boolean;
  position: number;
  createdAt: Date;
}

export const getLeadImages = async (leadId: string): Promise<LeadImage[]> => {
  try {
    const { data, error } = await supabase
      .from('lead_images')
      .select('*')
      .eq('lead_id', leadId)
      .order('position', { ascending: true });

    if (error) {
      console.error('Error fetching lead images:', error);
      throw error;
    }

    return (data || []).map(img => ({
      id: img.id,
      leadId: img.lead_id,
      imageUrl: img.image_url,
      isPrimary: img.is_primary,
      position: img.position,
      createdAt: new Date(img.created_at)
    }));
  } catch (error) {
    console.error('Error in getLeadImages:', error);
    toast({
      title: "Error",
      description: "Failed to load lead images",
      variant: "destructive",
    });
    return [];
  }
};

export const saveBulkLeadImages = async (
  leadId: string, 
  imageUrls: string[]
): Promise<LeadImage[]> => {
  try {
    // Prepare the images data for insertion - all as non-primary
    const leadImages = imageUrls.map((url, index) => ({
      lead_id: leadId,
      image_url: url,
      is_primary: false, // All images are non-primary
      position: index
    }));

    // Insert all images
    const { data, error } = await supabase
      .from('lead_images')
      .insert(leadImages)
      .select();

    if (error) {
      console.error('Error saving lead images:', error);
      throw error;
    }

    // Return the inserted images with updated formats
    return (data || []).map(img => ({
      id: img.id,
      leadId: img.lead_id,
      imageUrl: img.image_url,
      isPrimary: img.is_primary,
      position: img.position,
      createdAt: new Date(img.created_at)
    }));
  } catch (error) {
    console.error('Error in saveBulkLeadImages:', error);
    toast({
      title: "Error",
      description: "Failed to save lead images",
      variant: "destructive",
    });
    return [];
  }
};

export const setPrimaryImage = async (imageId: string, leadId: string): Promise<boolean> => {
  try {
    // First, set all images for this lead to non-primary
    const { error: resetError } = await supabase
      .from('lead_images')
      .update({ is_primary: false })
      .eq('lead_id', leadId);

    if (resetError) {
      console.error('Error resetting primary images:', resetError);
      throw resetError;
    }

    // Then set the selected image as primary
    const { error: updateError } = await supabase
      .from('lead_images')
      .update({ is_primary: true })
      .eq('id', imageId);

    if (updateError) {
      console.error('Error setting primary image:', updateError);
      throw updateError;
    }

    // Get the image URL of the selected primary image
    const { data: imageData, error: fetchError } = await supabase
      .from('lead_images')
      .select('image_url')
      .eq('id', imageId)
      .single();

    if (fetchError || !imageData) {
      console.error('Error fetching image URL:', fetchError);
      throw fetchError || new Error('Image not found');
    }

    // Update the main lead record with the new primary image URL
    const { error: leadUpdateError } = await supabase
      .from('leads')
      .update({ image_url: imageData.image_url })
      .eq('id', leadId);

    if (leadUpdateError) {
      console.error('Error updating lead image_url:', leadUpdateError);
      throw leadUpdateError;
    }

    return true;
  } catch (error) {
    console.error('Error in setPrimaryImage:', error);
    toast({
      title: "Error",
      description: "Failed to set primary image",
      variant: "destructive",
    });
    return false;
  }
};

export const deleteAllLeadImages = async (leadId: string): Promise<boolean> => {
  try {
    // Delete all images for the lead
    const { error: deleteError } = await supabase
      .from('lead_images')
      .delete()
      .eq('lead_id', leadId);

    if (deleteError) {
      console.error('Error deleting all lead images:', deleteError);
      throw deleteError;
    }

    // Update the lead to have no primary image
    const { error: updateError } = await supabase
      .from('leads')
      .update({ image_url: null })
      .eq('id', leadId);

    if (updateError) {
      console.error('Error updating lead with null image_url:', updateError);
      throw updateError;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteAllLeadImages:', error);
    return false;
  }
};

export const deleteLeadImage = async (imageId: string, leadId: string): Promise<boolean> => {
  try {
    // Check if the image to delete is primary
    const { data: imageData, error: fetchError } = await supabase
      .from('lead_images')
      .select('is_primary, image_url')
      .eq('id', imageId)
      .single();

    if (fetchError) {
      console.error('Error checking if image is primary:', fetchError);
      throw fetchError;
    }

    // Delete the image
    const { error: deleteError } = await supabase
      .from('lead_images')
      .delete()
      .eq('id', imageId);

    if (deleteError) {
      console.error('Error deleting image:', deleteError);
      throw deleteError;
    }

    // If deleted image was primary, set the first remaining image as primary and update lead.image_url
    if (imageData?.is_primary) {
      const { data: remainingImages, error: listError } = await supabase
        .from('lead_images')
        .select('id, image_url')
        .eq('lead_id', leadId)
        .order('position', { ascending: true })
        .limit(1);

      if (listError) {
        console.error('Error listing remaining images:', listError);
        throw listError;
      }

      if (remainingImages && remainingImages.length > 0) {
        // Set the first remaining image as primary
        await setPrimaryImage(remainingImages[0].id, leadId);
      } else {
        // No images left, update lead to have no image
        const { error: updateError } = await supabase
          .from('leads')
          .update({ image_url: null })
          .eq('id', leadId);

        if (updateError) {
          console.error('Error updating lead with null image_url:', updateError);
          throw updateError;
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error in deleteLeadImage:', error);
    toast({
      title: "Error",
      description: "Failed to delete image",
      variant: "destructive",
    });
    return false;
  }
};
